import clsx from 'clsx';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RepairStatus } from '../enums/repair-status';
import { formatNumber } from '../services/helpers';
import myToastr from '../services/toastr';
import { useAppSelector } from '../store/hooks';
import {
  deliverNewRepair,
  repairSelector,
  setErrorCostPrice,
  setErrorItem,
  setErrorRepairPrice,
  updateRepair,
  updateRepairAcceptBudget,
  updateRepairBudgetInStore,
  updateRepairPrices,
  updateRepairReceivedFromTechnicalService,
  updateRepairSentTechnicalService,
  updateRepairWithWarranty,
} from '../store/repair-slice';
import PinModal from './pin-modal';
import RecipientModal from './recipient-modal';

const UpdateRepairButton = () => {
  const dispatch = useDispatch();
  const { requesting, repair, item, repairConcept, paymentsOnAccount } = useSelector(repairSelector);
  const [showPinModal, setShowPinModal] = useState<boolean>(false);
  const disabled: boolean = useMemo(() => {
    if (requesting) {
      return true;
    }
    if (repair!.item && !item) {
      return true;
    }
    let someDataChanged = false;
    if (repair!.item !== item) {
      someDataChanged = true;
    }
    if (repair!.repairConcept !== repairConcept) {
      someDataChanged = true;
    }
    if (paymentsOnAccount.length > 0) {
      someDataChanged = true;
    }
    return !someDataChanged;
  }, [requesting, repair, item, repairConcept, paymentsOnAccount]);

  const openPinModal = () => {
    if (repair!.item && !item) {
      myToastr.error(`El nombre del producto es obligatorio`);
      dispatch(setErrorItem(true));
      return;
    }
    let someDataChanged = false;
    if (repair!.item !== item) {
      someDataChanged = true;
    }
    if (repair!.repairConcept !== repairConcept) {
      someDataChanged = true;
    }
    if (paymentsOnAccount.length > 0) {
      someDataChanged = true;
    }
    if (!someDataChanged) {
      return;
    }
    setShowPinModal(true);
  };

  const onUpdateRepair = async (pin: string | null) => {
    setShowPinModal(false);
    if (!pin) {
      return;
    }
    await dispatch(updateRepair({ pin }));
    myToastr.success('Compostura actualizada');
    window.location.reload();
  };

  return (
    <React.Fragment>
      <button className={clsx('save', { disabled })} onClick={openPinModal}>
        Guardar
      </button>
      <PinModal show={showPinModal} onCloseModal={onUpdateRepair} />
    </React.Fragment>
  );
};

const UpdateRepairButtons = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const organization = useAppSelector((state) => state.auth.organization!);
  const { requesting, pending, repair, costPrice, repairPrice, acceptBudget, sentTechnicalService, receivedFromTechnicalService } = useSelector(repairSelector);
  const [showPinModalSentTS, setShowPinModalSentTS] = useState<boolean>(false);
  const [showPinModalSentTS2, setShowPinModalSentTS2] = useState<boolean>(false);
  const [showPinModalReceivedFromTS, setShowPinModalReceivedFromTS] = useState<boolean>(false);
  const [showPinModalReceivedFromTSWithWarranty, setShowPinModalReceivedFromTSWithWarranty] = useState<boolean>(false);
  const [showPinModalBudgetPrice, setShowPinModalBudgetPrice] = useState<boolean>(false);
  const [showPinModalAcceptBudget, setShowPinModalAcceptBudget] = useState<boolean>(false);
  const [showPinModalBudgetInStore, setShowPinModalBudgetInStore] = useState<boolean>(false);
  const [showPinModalDeliverRepair, setShowPinModalDeliveryRepair] = useState<boolean>(false);
  const [showRecipientModal, setShowRecipientModal] = useState(false);

  const disabledNoWarrantySentTS: boolean = useMemo(() => {
    return !sentTechnicalService;
  }, [sentTechnicalService]);

  const disabledNoWarrantyReceivedFromTSButton: boolean = useMemo(() => {
    return !receivedFromTechnicalService;
  }, [receivedFromTechnicalService]);

  const disabledBudgetPriceButton: boolean = useMemo(() => {
    return costPrice === null || repairPrice === null;
  }, [costPrice, repairPrice]);

  const disabledAcceptRejectButton: boolean = useMemo(() => {
    return acceptBudget === null;
  }, [acceptBudget]);

  const onUpdateRepairSentTechnicalService = async (pin: string | null) => {
    await dispatch(
      updateRepairSentTechnicalService({
        organizationId: organization.id,
        pin,
        sentTechnicalService,
      }),
    );
  };

  const onUpdateRepairWithWarranty = async (pin: string | null) => {
    await dispatch(updateRepairWithWarranty({ organizationId: organization.id, pin, sentTechnicalService }));
  };

  const onUpdateRepairReceivedFromTechnicalService = async (pin: string | null) => {
    await dispatch(updateRepairReceivedFromTechnicalService({ organizationId: organization.id, pin, receivedFromTechnicalService }));
  };

  const onClosePinModalReceivedFromSTWithWarranty = async (pin: string | null) => {
    await dispatch(updateRepairWithWarranty({ organizationId: organization.id, pin, receivedByTechnicalService: receivedFromTechnicalService }));
  };

  const onUpdateRepairPrices = async (pin: string | null) => {
    await dispatch(updateRepairPrices({ organizationId: organization.id, pin, costPrice: costPrice!, repairPrice: repairPrice! }));
  };

  const onUpdateRepairAcceptBudget = async (pin: string | null) => {
    await dispatch(updateRepairAcceptBudget({ organizationId: organization.id, pin, accept: acceptBudget! }));
  };

  const onUpdateRepairBudgetInStore = async (pin: string | null) => {
    await dispatch(updateRepairBudgetInStore({ organizationId: organization.id, pin }));
    window.location.reload();
  };

  const deliveryRepair = () => {
    if (pending > 0 && repair!.acceptBudget) {
      myToastr.error(`Faltan por abonar ${formatNumber(pending)}€`);
      return;
    }
    const MAX_SALE_AMOUNT = parseFloat(process.env.REACT_APP_MAX_SALE_AMOUNT!);
    if (repair!.repairPrice > MAX_SALE_AMOUNT) {
      setShowRecipientModal(true);
      return;
    }
    if (organization.requireTransactionPin) {
      setShowPinModalDeliveryRepair(true);
      return;
    }
    onDeliveryNewRepair(null);
  };

  const onSaveRecipientModal = () => {
    setShowRecipientModal(false);
    setTimeout(() => {
      if (organization.requireTransactionPin) {
        setShowPinModalDeliveryRepair(true);
        return;
      }
      onDeliveryNewRepair(null);
    }, 500);
  };

  const onDeliveryNewRepair = async (pin: string | null) => {
    await dispatch(deliverNewRepair(pin));
  };

  return (
    <div className="update-repair-buttons">
      {!repair?.warranty && repair?.repairStatus === RepairStatus.Budget && repair?.sentTechnicalService === null && (
        <React.Fragment>
          <UpdateRepairButton />
          <button
            onClick={() => {
              if (disabledNoWarrantySentTS) {
                myToastr.error('Producto no enviado al servicio técnico');
                return;
              }
              if (organization.requireTransactionPin) {
                setShowPinModalSentTS(true);
                return;
              }
              onUpdateRepairSentTechnicalService(null);
            }}
            disabled={requesting}
            className={clsx('container-button pick-up-repair', { disabled: disabledNoWarrantySentTS })}
          >
            Siguiente
          </button>
        </React.Fragment>
      )}
      {repair?.warranty === false && repair?.repairStatus === RepairStatus.InProcess && repair?.sentTechnicalService && repair?.costPrice !== null && repair?.repairPrice !== null && (
        <React.Fragment>
          <UpdateRepairButton />
          <button
            onClick={() => {
              if (disabledNoWarrantyReceivedFromTSButton) {
                myToastr.error('El producto no se ha recibido del servicio técnico');
                return;
              }
              if (organization.requireTransactionPin) {
                setShowPinModalReceivedFromTS(true);
                return;
              }
              onUpdateRepairReceivedFromTechnicalService(null);
            }}
            disabled={requesting}
            className={clsx('container-button pick-up-repair', { disabled: disabledNoWarrantyReceivedFromTSButton })}
          >
            Pasar a recogida
          </button>
        </React.Fragment>
      )}
      {repair?.warranty === false && repair?.repairStatus === RepairStatus.Budget && repair.sentTechnicalService && repair.costPrice == null && repair.repairPrice === null && (
        <React.Fragment>
          <UpdateRepairButton />
          <button
            onClick={() => {
              if (costPrice === null) {
                myToastr.error('Introduce el precio de coste de la reparación');
                dispatch(setErrorCostPrice(true));
              }
              if (repairPrice === null) {
                myToastr.error('Introduce el precio de la reparación');
                dispatch(setErrorRepairPrice(true));
              }
              if (disabledBudgetPriceButton) {
                return;
              }
              if (organization.requireTransactionPin) {
                setShowPinModalBudgetPrice(true);
                return;
              }
              onUpdateRepairPrices(null);
            }}
            disabled={requesting}
            className={clsx('container-button next', { disabled: disabledBudgetPriceButton })}
          >
            Guardar presupuesto
          </button>
        </React.Fragment>
      )}
      {repair?.warranty === false && repair?.repairStatus === RepairStatus.Budget && repair.costPrice !== null && repair.repairPrice !== null && (
        <React.Fragment>
          <UpdateRepairButton />
          <button
            onClick={() => {
              if (acceptBudget === null) {
                myToastr.error('Acepta o rechaza el presupuesto');
                return;
              }
              if (disabledAcceptRejectButton) {
                return;
              }
              if (organization.requireTransactionPin) {
                setShowPinModalAcceptBudget(true);
                return;
              }
              onUpdateRepairAcceptBudget(null);
            }}
            disabled={requesting}
            className={clsx('container-button next', { disabled: disabledAcceptRejectButton })}
          >
            Siguiente
          </button>
        </React.Fragment>
      )}
      {repair?.warranty && repair?.repairStatus === RepairStatus.InProcess && repair?.sentTechnicalService === null && (
        <React.Fragment>
          <UpdateRepairButton />
          <button
            onClick={() => {
              if (sentTechnicalService !== true) {
                myToastr.error('Envía el producto al servicio técnico');
                return;
              }
              if (organization.requireTransactionPin) {
                setShowPinModalSentTS2(true);
                return;
              }
              onUpdateRepairWithWarranty(null);
            }}
            disabled={requesting}
            className={clsx('container-button next', { disabled: sentTechnicalService !== true })}
          >
            Siguiente
          </button>
        </React.Fragment>
      )}
      {repair?.warranty && repair?.sentTechnicalService === true && repair?.receivedFromTechnicalService !== true && (
        <React.Fragment>
          <UpdateRepairButton />
          <button
            onClick={() => {
              if (receivedFromTechnicalService !== true) {
                myToastr.error('El producto no se ha recibido del servicio técnico');
                return;
              }
              if (organization.requireTransactionPin) {
                setShowPinModalReceivedFromTSWithWarranty(true);
                return;
              }
              onClosePinModalReceivedFromSTWithWarranty(null);
            }}
            disabled={requesting}
            className={clsx('container-button next', { disabled: receivedFromTechnicalService !== true })}
          >
            Siguiente
          </button>
        </React.Fragment>
      )}
      {repair?.warranty === false && repair?.repairStatus === RepairStatus.InProcess && repair.budgetInStore && (
        <React.Fragment>
          <UpdateRepairButton />
          <button
            onClick={() => {
              if (organization.requireTransactionPin) {
                setShowPinModalBudgetInStore(true);
                return;
              }
              onUpdateRepairBudgetInStore(null);
            }}
            disabled={requesting}
            className={clsx('container-button pick-up-repair')}
          >
            Pasar a recogida
          </button>
        </React.Fragment>
      )}
      {repair?.repairStatus === RepairStatus.Canceled && (
        <React.Fragment>
          <UpdateRepairButton />
          <button onClick={() => navigate('/reparaciones')} disabled={requesting} className={clsx('container-button back')}>
            Volver
          </button>
        </React.Fragment>
      )}
      {repair?.repairStatus === RepairStatus.Finished && (
        <React.Fragment>
          <UpdateRepairButton />
          <button onClick={() => deliveryRepair()} disabled={requesting} className={clsx('container-button pick-up-repair', { disabled: requesting || (pending > 0 && repair.acceptBudget) })}>
            Retirado por el cliente
          </button>
        </React.Fragment>
      )}
      {repair?.repairStatus === RepairStatus.Delivered && (
        <React.Fragment>
          <UpdateRepairButton />
          <button onClick={() => navigate('/reparaciones')} disabled={requesting} className={clsx('container-button back')}>
            Volver
          </button>
        </React.Fragment>
      )}
      <PinModal
        show={showPinModalSentTS}
        onCloseModal={(pin) => {
          setShowPinModalSentTS(false);
          if (pin == null || pin.length !== 4) {
            return;
          }
          onUpdateRepairSentTechnicalService(pin);
        }}
      />
      <PinModal
        show={showPinModalSentTS2}
        onCloseModal={(pin) => {
          setShowPinModalSentTS2(false);
          if (pin == null || pin.length !== 4) {
            return;
          }
          onUpdateRepairWithWarranty(pin);
        }}
      />
      <PinModal
        show={showPinModalReceivedFromTS}
        onCloseModal={(pin) => {
          setShowPinModalReceivedFromTS(false);
          if (pin == null || pin.length !== 4) {
            return;
          }
          onUpdateRepairReceivedFromTechnicalService(pin);
        }}
      />
      <PinModal
        show={showPinModalReceivedFromTSWithWarranty}
        onCloseModal={(pin) => {
          setShowPinModalReceivedFromTSWithWarranty(false);
          if (pin == null || pin.length !== 4) {
            return;
          }
          onClosePinModalReceivedFromSTWithWarranty(pin);
        }}
      />
      <PinModal
        show={showPinModalBudgetPrice}
        onCloseModal={(pin) => {
          setShowPinModalBudgetPrice(false);
          if (pin == null || pin.length !== 4) {
            return;
          }
          onUpdateRepairPrices(pin);
        }}
      />
      <PinModal
        show={showPinModalAcceptBudget}
        onCloseModal={(pin) => {
          setShowPinModalAcceptBudget(false);
          if (pin == null || pin.length !== 4) {
            return;
          }
          onUpdateRepairAcceptBudget(pin);
        }}
      />
      <PinModal
        show={showPinModalBudgetInStore}
        onCloseModal={(pin) => {
          setShowPinModalBudgetInStore(false);
          if (pin == null || pin.length !== 4) {
            return;
          }
          onUpdateRepairBudgetInStore(pin);
        }}
      />
      <PinModal
        show={showPinModalDeliverRepair}
        onCloseModal={(pin) => {
          setShowPinModalDeliveryRepair(false);
          if (pin == null || pin.length !== 4) {
            return;
          }
          onDeliveryNewRepair(pin);
        }}
      />
      <RecipientModal onClose={() => setShowRecipientModal(false)} onSave={onSaveRecipientModal} open={showRecipientModal} type="repair" />
    </div>
  );
};

export default UpdateRepairButtons;
